<script setup>
import { usePlatformApi } from '@/utils/platformApi'

// Addendum 14, Modul BK. Sama pola auth-check & dua lapis keamanan dengan
// halaman /platform lain -- lihat komentar di platform/tenants/index.vue.
definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

const PRIORITY_META = {
  low: { label: 'Rendah', color: 'secondary' },
  medium: { label: 'Sedang', color: 'info' },
  high: { label: 'Tinggi', color: 'warning' },
  urgent: { label: 'Urgent', color: 'error' },
}

const STATUS_META = {
  open: { label: 'Terbuka', color: 'info' },
  in_progress: { label: 'Diproses', color: 'warning' },
  resolved: { label: 'Selesai', color: 'success' },
  closed: { label: 'Ditutup', color: 'secondary' },
}

const STATUS_OPTIONS = [
  { title: 'Semua Status', value: null },
  { title: 'Terbuka', value: 'open' },
  { title: 'Diproses', value: 'in_progress' },
  { title: 'Selesai', value: 'resolved' },
  { title: 'Ditutup', value: 'closed' },
]

const PRIORITY_OPTIONS = [
  { title: 'Semua Prioritas', value: null },
  { title: 'Urgent', value: 'urgent' },
  { title: 'Tinggi', value: 'high' },
  { title: 'Sedang', value: 'medium' },
  { title: 'Rendah', value: 'low' },
]

// ─── State ────────────────────────────────────────────────────────────────
const tickets = ref([])
const loading = ref(false)
const page = ref(1)
const perPage = ref(20)
const totalItems = ref(0)

const filterStatus = ref(null)
const filterPriority = ref(null)

const formatDate = iso =>
  iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'

const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

const fetchTickets = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (filterStatus.value) query.status = filterStatus.value
    if (filterPriority.value) query.priority = filterPriority.value

    const res = await usePlatformApi('/api/v1/platform/tickets', { query }).get()
    const body = res?.data ?? res

    tickets.value = body?.data ?? []
    totalItems.value = body?.pagination?.total ?? 0
  } catch (err) {
    if (await handleAuthError(err)) return

    console.error('[Platform] fetch tickets error', err)
    tickets.value = []
  } finally {
    loading.value = false
  }
}

watch([page, filterStatus, filterPriority], fetchTickets, { immediate: true })

const headers = [
  { title: 'Tenant', key: 'tenant', sortable: false },
  { title: 'Subjek', key: 'subject', sortable: false },
  { title: 'Prioritas', key: 'priority', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Dibuat', key: 'created_at', sortable: false },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: 100 },
]
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4">
      <div>
        <h4 class="text-h4">
          Tiket Dukungan
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Login sebagai {{ platformAdminData?.name }}
        </p>
      </div>
    </div>

    <VCard>
      <VCardText class="text-body-2 text-medium-emphasis">
        Daftar tiket dukungan dari SEMUA tenant. Urutan default: status belum selesai
        (Terbuka/Diproses) duluan, di dalam grup itu prioritas tertinggi (Urgent) duluan.
      </VCardText>

      <VDivider />

      <VCardText>
        <VRow>
          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <VSelect
              v-model="filterStatus"
              :items="STATUS_OPTIONS"
              label="Filter Status"
              hide-details
              density="compact"
            />
          </VCol>
          <VCol
            cols="12"
            sm="6"
            md="3"
          >
            <VSelect
              v-model="filterPriority"
              :items="PRIORITY_OPTIONS"
              label="Filter Prioritas"
              hide-details
              density="compact"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:page="page"
        :headers="headers"
        :items="tickets"
        :items-length="totalItems"
        :loading="loading"
        :items-per-page="perPage"
        no-data-text="Tidak ada tiket yang cocok dengan filter."
      >
        <template #item.tenant="{ item }">
          {{ item.tenant?.name ?? '-' }}
        </template>

        <template #item.subject="{ item }">
          <RouterLink
            :to="{ name: 'platform-tickets-id', params: { id: item.id } }"
            class="text-decoration-none font-weight-medium"
          >
            {{ item.subject }}
          </RouterLink>
        </template>

        <template #item.priority="{ item }">
          <VChip
            :color="PRIORITY_META[item.priority]?.color"
            variant="tonal"
            size="small"
          >
            {{ PRIORITY_META[item.priority]?.label ?? item.priority }}
          </VChip>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="STATUS_META[item.status]?.color"
            variant="tonal"
            size="small"
          >
            {{ STATUS_META[item.status]?.label ?? item.status }}
          </VChip>
        </template>

        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template #item.actions="{ item }">
          <VBtn
            icon="ri-eye-line"
            variant="text"
            size="small"
            color="primary"
            :to="{ name: 'platform-tickets-id', params: { id: item.id } }"
          />
        </template>
      </VDataTableServer>
    </VCard>
  </div>
</template>
